import { FileText, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  }).format(Number(n ?? 0));
}

/** Line amounts arrive as numbers (r2) or preformatted strings. */
function fmtAmount(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'number') return formatEur(v);
  return v;
}

/**
 * CreateQuotePreview — rich two-step approval card for CREATE_QUOTE (cotización).
 *
 * The generic PendingActionCard drops the nested `lineas` array, so the user
 * could not review the quote line items before it was created. This card mirrors
 * CreateInvoicePreview: a line-items table + subtotal/IVA/total breakdown, plus
 * client, series and validity date.
 *
 * Backend preview shape (raw keys via toolExecutor `_raw`):
 *   { cliente, deal, series, valida_hasta, subtotal, iva, total, notas,
 *     lineas: [{ descripcion, cantidad, precio_unitario, iva, descuento, subtotal }] }
 *
 * Props: action { type, preview }, onApprove, onReject, status
 */
export default function CreateQuotePreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <FileText className="w-3.5 h-3.5" />
        <span>Cotización aprobada — creando borrador…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Creación de cotización cancelada</span>
      </div>
    );
  }

  const lineas = preview?.lineas ?? [];

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            Requiere aprobación
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight">
            {preview?.cliente ?? '—'}
          </p>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Cotización{preview?.series ? ` · serie ${preview.series}` : ''}
            {preview?.valida_hasta ? ` · válida hasta ${preview.valida_hasta}` : ''}
          </p>
        </div>
        <span className="text-lg font-bold text-indigo-700 dark:text-indigo-300 shrink-0 tabular-nums">
          {fmtAmount(preview?.total)}
        </span>
      </div>

      {/* Line items table */}
      {lineas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-indigo-200 dark:border-indigo-800 text-muted-foreground">
                <th className="text-left pb-1 font-medium">Concepto</th>
                <th className="text-right pb-1 font-medium">Ud.</th>
                <th className="text-right pb-1 font-medium">P.U.</th>
                <th className="text-right pb-1 font-medium">IVA</th>
                <th className="text-right pb-1 font-medium">Importe</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-indigo-100 dark:divide-indigo-900/40">
              {lineas.map((l, i) => (
                <tr key={i}>
                  <td className="py-1 pr-2 text-foreground max-w-[120px] truncate">
                    {l.descripcion}
                    {l.descuento && <span className="block text-[9px] text-muted-foreground">dto. {l.descuento}</span>}
                  </td>
                  <td className="py-1 text-right text-muted-foreground">{l.cantidad}</td>
                  <td className="py-1 text-right text-muted-foreground tabular-nums">{fmtAmount(l.precio_unitario)}</td>
                  <td className="py-1 text-right text-muted-foreground">{l.iva}</td>
                  <td className="py-1 text-right font-medium text-foreground tabular-nums">{fmtAmount(l.subtotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Totals */}
      <div className="flex flex-col gap-0.5 text-xs border-t border-indigo-200 dark:border-indigo-800 pt-2">
        <div className="flex justify-between text-muted-foreground">
          <span>Base imponible</span>
          <span>{fmtAmount(preview?.subtotal)}</span>
        </div>
        <div className="flex justify-between text-muted-foreground">
          <span>IVA</span>
          <span>{fmtAmount(preview?.iva)}</span>
        </div>
        <div className="flex justify-between font-semibold text-foreground">
          <span>Total</span>
          <span>{fmtAmount(preview?.total)}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
          <XCircle className="w-3 h-3 mr-1" />Cancelar
        </Button>
        <Button size="sm" onClick={() => onApprove()}
          className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
          <FileText className="w-3 h-3 mr-1" />Crear cotización
        </Button>
      </div>
    </div>
  );
}
